import { useState } from 'react';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import type { FlagChange, FlagDistrict, FlagScope, FlagState } from './featureFlagsApi';
import { describeState, districtName } from './featureFlagsView';
import { SIS, sisText } from '../SisMatching/sisTokens';

export type FlagDraft = { enabled: boolean; scope: FlagScope; districtIds: string[] };

type Props = {
  env: string;
  releaseName: string;
  flagKey: string;
  current: FlagState | null;
  initial: FlagDraft;
  districts: FlagDistrict[];
  saving: boolean;
  error: string | null;
  onConfirm: (change: FlagChange) => void;
  onCancel: () => void;
};

const NOTE_MAX = 280;

function draftState(draft: FlagDraft, current: FlagState | null): FlagState {
  return { enabled: draft.enabled, scope: draft.scope, districtIds: draft.districtIds, updatedAt: '', updatedBy: '', note: '', revision: current?.revision ?? 0 };
}

export function FlagDialog({ env, releaseName, flagKey, current, initial, districts, saving, error, onConfirm, onCancel }: Props) {
  const [enabled, setEnabled] = useState(initial.enabled);
  const [scope, setScope] = useState<FlagScope>(initial.scope);
  const [districtIds, setDistrictIds] = useState<string[]>(initial.districtIds);
  const [note, setNote] = useState('');
  const [newId, setNewId] = useState('');

  const choices = [...districts.map((d) => d.id), ...districtIds.filter((id) => !districts.some((d) => d.id === id))];
  const noteLength = Array.from(note.trim()).length;
  const noteReady = noteLength > 0 && noteLength <= NOTE_MAX;
  const scopeReady = !enabled || scope === 'all' || districtIds.length > 0;
  const canConfirm = noteReady && scopeReady && !saving;

  const toggle = (id: string) => {
    setDistrictIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].sort()));
  };

  const addById = () => {
    const id = newId.trim();
    if (id === '') return;
    setDistrictIds((ids) => (ids.includes(id) ? ids : [...ids, id].sort()));
    setNewId('');
  };

  const confirm = () => {
    onConfirm({ enabled, scope, districtIds: scope === 'districts' ? [...districtIds].sort() : [], note: note.trim(), expectedRevision: current?.revision ?? 0 });
  };

  return (
    <Dialog open onClose={saving ? undefined : onCancel} aria-labelledby="flag-dialog-title" PaperProps={{ sx: { borderRadius: 2, maxWidth: 520, width: '100%', p: 1 } }}>
      <DialogTitle id="flag-dialog-title" sx={{ ...sisText.h4, pb: 1 }}>{`Switch ${flagKey}`}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Typography sx={{ ...sisText.body2, color: SIS.muted }}>{`${env} · ${releaseName}`}</Typography>
        <Box sx={{ display: 'flex', gap: '24px' }}>
          <Typography sx={sisText.body1}>
            Before: <Box component="span" data-testid="flag-before" sx={{ fontWeight: 700 }}>{describeState(current)}</Box>
          </Typography>
          <Typography sx={sisText.body1}>
            After: <Box component="span" data-testid="flag-after" sx={{ fontWeight: 700 }}>{describeState(draftState({ enabled, scope, districtIds }, current))}</Box>
          </Typography>
        </Box>
        <FormControlLabel control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />} label="Switched on" />
        {enabled && (
          <RadioGroup aria-label="Who gets it" value={scope} onChange={(e) => setScope(e.target.value as FlagScope)}>
            <FormControlLabel value="all" control={<Radio />} label="All districts" />
            <FormControlLabel value="districts" control={<Radio />} label="Selected districts" />
          </RadioGroup>
        )}
        {enabled && scope === 'districts' && (
          <Box role="group" aria-label="Districts" sx={{ display: 'flex', flexDirection: 'column', maxHeight: 240, overflowY: 'auto', border: `1px solid ${SIS.divider}`, borderRadius: '8px', padding: '4px 12px' }}>
            {choices.map((id) => (
              <FormControlLabel key={id} control={<Checkbox checked={districtIds.includes(id)} onChange={() => toggle(id)} />} label={districtName(id, districts)} />
            ))}
            {choices.length === 0 && <Typography sx={{ ...sisText.caption, padding: '8px 0' }}>No districts are listed yet.</Typography>}
          </Box>
        )}
        {enabled && scope === 'districts' && (
          <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <TextField id="flag-district-id" label="Add a district by id" size="small" value={newId} onChange={(e) => setNewId(e.target.value)} helperText="For a district the audit report leaves out, such as a test district." sx={{ flex: 1 }} />
            <Button onClick={addById} disabled={newId.trim() === ''} sx={{ textTransform: 'none', fontWeight: 600, marginTop: '4px' }}>Add</Button>
          </Box>
        )}
        <TextField id="flag-note" label="Why" multiline minRows={2} value={note} onChange={(e) => setNote(e.target.value)} error={noteLength > NOTE_MAX} helperText={`${noteLength}/${NOTE_MAX}. Goes into the event log and Slack.`} />
        {error && <Typography role="alert" sx={{ ...sisText.body2, color: SIS.warningDark }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: SIS.primary }}>Cancel</Button>
        <Button variant="contained" onClick={confirm} disabled={!canConfirm} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 16, px: 3, borderRadius: 1.5 }}>Confirm</Button>
      </DialogActions>
    </Dialog>
  );
}
